import { MCPermissions } from "~/common/constants";
import { BuildHtml } from "~/components/BuildHtml";
import { ContentSummarySidecar } from "~/components/Sidecar/ContentSummarySidecar/ContentSummarySidecar";
import { KeyTopicsSidecar } from "~/components/Sidecar/KeyTopicsSidecar/KeyTopicsSidecarBlock";
import { PostListSidecar } from "~/components/Sidecar/PostListSidecar/PostListSidecar";
import {
  isKeyTopicsBlock,
  isPianoBlock,
  isPostListBlock,
  isRelatedReportBlock,
  isWysiwygBlock,
  SidecarBlock as SidecarBlockType,
  WysiwygBlock,
} from "~/components/Sidecar/sidecar-schemas";
import {
  SidecarContainer,
  SidecarContainerProps,
} from "~/components/Sidecar/SidecarContainer";
import { sidecarTagToClassNameMap } from "~/components/Sidecar/sidecarTagToClassNameMap";
import { sidecarTagToReactConverters } from "~/components/Sidecar/sidecarTagToReactConverters";
import { InvalidProperties } from "~/landing-pages/components/Errors/InvalidProperties/InvalidProperties";
import { UnknownBlockType } from "~/landing-pages/components/Errors/UnknownBlockType/UnknownBlockType";
import { sanitizeRelativeUrl } from "~/lib/shared/sanitizeRelativeUrl";
import { Body, Button, FullWidthImage, Link } from "~/ui-library";
import { ChevronRightIcon } from "~/ui-library/icons";

const OptionalCta = (block: WysiwygBlock) => {
  if (!block.optionalCta) {
    return null;
  }

  const optionalCtaStyle = block.optionalCtaStyle ?? "text_link";
  const { url, title } = block.optionalCta;

  if (optionalCtaStyle === "text_link") {
    return (
      <div className="flex justify-end print:hidden">
        <Link
          href={sanitizeRelativeUrl(block.optionalCta.url)}
          className="flex w-fit gap-xxs"
          endAdornmentIcon={ChevronRightIcon}
        >
          <Body size="large" mdSize="small">
            {block.optionalCta.title}
          </Body>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex justify-center print:hidden">
      <Link href={sanitizeRelativeUrl(url)}>
        <Button size="extraSmall" variant="outline">
          {title}
        </Button>
      </Link>
    </div>
  );
};

export type SidecarBlockProps = {
  block: SidecarBlockType;
  blockIndex: number;
} & Pick<SidecarContainerProps, "border" | "gutters">;

export const SidecarBlock: React.FC<SidecarBlockProps> = ({
  block,
  blockIndex,
  border,
  gutters,
}): JSX.Element | null => {
  const blockInfo = { blockIndex, blockType: block.acfFcLayout };
  const blockKey = `${blockInfo.blockType}-${blockInfo.blockIndex}`;
  if (isKeyTopicsBlock(block)) {
    return (
      <SidecarContainer border={border} gutters={gutters} key={blockKey}>
        <KeyTopicsSidecar key={blockKey} tags={block.topics} />
      </SidecarContainer>
    );
  }

  if (block.acfFcLayout === "newsletter_cta") {
    return (
      <SidecarContainer border={border} gutters={gutters} key={blockKey}>
        Newsletter CTA Placeholder
      </SidecarContainer>
    );
  }

  if (isPianoBlock(block)) {
    return (
      <SidecarContainer border={border} gutters={gutters} key={blockKey}>
        <div key={blockKey} id={block.targetId} />
      </SidecarContainer>
    );
  }

  if (isPostListBlock(block)) {
    return (
      <SidecarContainer border={border} gutters={gutters} key={blockKey}>
        <PostListSidecar {...block} />
      </SidecarContainer>
    );
  }

  if (isRelatedReportBlock(block)) {
    return (
      <SidecarContainer border={border} gutters={gutters} key={blockKey}>
        <ContentSummarySidecar
          contentHtml={block.wysiwyg}
          downloadButtonProps={{
            attachmentId: block.attachmentId,
            postId: block.postId,
            dataAccess: block.dataAccess as MCPermissions[],
          }}
        />
      </SidecarContainer>
    );
  }

  if (isWysiwygBlock(block)) {
    const bgColorValue = block.backgroundColor
      ? block.backgroundColor
      : undefined;

    return (
      <SidecarContainer
        border={border}
        gutters={gutters}
        key={blockKey}
        style={{ backgroundColor: bgColorValue }}
      >
        <BuildHtml
          html={block.wysiwyg}
          tagToClassNameMap={sidecarTagToClassNameMap}
          tagToReactConverters={sidecarTagToReactConverters}
        />
        {!!block.optionalImage && !!block.optionalImageLink && (
          <Link href={sanitizeRelativeUrl(block.optionalImageLink.url)}>
            <FullWidthImage
              src={block.optionalImage}
              aspectRatio={block.optionalImageOrientation}
            />
          </Link>
        )}
        {!!block.optionalImage && !block.optionalImageLink && (
          <FullWidthImage
            src={block.optionalImage}
            aspectRatio={block.optionalImageOrientation}
          />
        )}

        <OptionalCta {...block} />
      </SidecarContainer>
    );
  }

  if (block.acfFcLayout === "invalid_properties") {
    return <InvalidProperties {...block} className="whitespace-normal" />;
  }

  if (block.acfFcLayout === "unknown_block") {
    return <UnknownBlockType block={block} className="whitespace-normal" />;
  }

  return null;
};
